import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  signal,
  viewChild
} from '@angular/core';

/**
 * Blur-up image: loads a small thumbnail first, shows it blurred (CSS), and
 * cross-fades to the full image once it loads.
 *
 * - `src`         full-resolution image URL
 * - `placeholder` small thumbnail URL shown blurred while `src` loads (AppUpdate.imageThumbUrl)
 * - `imgClass`    tailwind classes applied to both layers (sizing, rounding, hover…)
 * - `fill`        true → the host is positioned absolutely to fill its (positioned)
 *                 parent and both layers cover it (use for background-style images);
 *                 false → the full image flows normally and defines the height.
 *
 * Position/inset are driven by inline host-style bindings so they win over
 * utility classes regardless of stylesheet injection order.
 */
@Component({
  selector: 'app-blur-image',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.display]': "'block'",
    '[style.overflow]': "'hidden'",
    '[style.position]': "fill() ? 'absolute' : 'relative'",
    '[style.inset]': "fill() ? '0' : null"
  },
  template: `
    @if (placeholder()) {
      <img
        [src]="placeholder()"
        alt=""
        aria-hidden="true"
        [class]="'bi-img bi-placeholder ' + (imgClass() || '')"
      />
    }
    <img
      #full
      [src]="src()"
      [alt]="alt()"
      [attr.loading]="loading()"
      [class]="
        'bi-img bi-full ' +
        (fill() ? 'bi-fill ' : '') +
        (loaded() ? 'bi-loaded ' : '') +
        (imgClass() || '')
      "
      (load)="markLoaded()"
      (error)="markLoaded()"
    />
  `,
  styles: `
    .bi-placeholder {
      position: absolute !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      object-fit: cover;
      filter: blur(14px);
      transform: scale(1.08);
      z-index: 0;
    }
    .bi-full {
      position: relative;
      z-index: 1;
      opacity: 0;
      transition: opacity 0.45s ease, transform 0.3s ease;
    }
    .bi-full.bi-fill {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .bi-full.bi-loaded {
      opacity: 1;
    }
  `
})
export class BlurImageComponent implements AfterViewInit {
  src = input<string | undefined>(undefined);
  placeholder = input<string | undefined>(undefined);
  alt = input<string>('');
  imgClass = input<string>('');
  fill = input<boolean>(false);
  loading = input<'lazy' | 'eager' | null>(null);

  protected loaded = signal(false);
  private full = viewChild<ElementRef<HTMLImageElement>>('full');

  ngAfterViewInit(): void {
    // Cached images may not fire (load); reconcile from the element state.
    const el = this.full()?.nativeElement;
    if (el?.complete && el.naturalWidth > 0) this.markLoaded();
  }

  markLoaded(): void {
    this.loaded.set(true);
  }
}
